import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  type AuditEventResponse,
  COUNTRIES,
  type ContactResponse,
  type RegistryLookups,
  type RoleDataField,
  type SubjectResponse,
  type SubjectRoleResponse,
  NO_INVOICE_DOCUMENT_LABELS,
  NO_INVOICE_DOCUMENTS,
  type NoInvoiceDocument,
} from '@k-management/shared';
import { type FormEvent, type ReactNode, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { useCan } from '../../auth/AuthProvider.js';
import { ConfirmDialog } from '../../components/ConfirmDialog.js';
import { Field } from '../../components/Field.js';
import { type Command, useCommands, usePageTitle } from '../../components/layout/page-chrome.js';
import { apiRequest } from '../../lib/api.js';
import { describeAuditDetails } from '../../lib/audit-format.js';
import { formatTimestamp } from '../../lib/format.js';
import { showServerError, type FieldErrors } from '../../lib/form-validation.js';
import { ClientPriceLists } from './ClientPriceLists.js';
import { ROLE_FIELD_LABELS, RoleFields } from './RoleFields.js';
import { StatementView } from '../treasury/StatementView.js';
import {
  SubjectDocuments,
  useSubjectFiles,
  useSubjectJobs,
  useSubjectQuotes,
} from './SubjectDocuments.js';
import {
  lookupLabel,
  registryKeys,
  RoleBadge,
  StatePill,
  useRegistryLookups,
} from './registry-ui.js';

type Tab =
  | 'general'
  | `role:${string}`
  | 'classifications'
  | 'contacts'
  | 'aliases'
  | 'documents'
  | 'statement'
  | 'history';

/** The subject sheet (proposal §2.5): general data, a tab per role, classifications, contacts, aliases, history. */
export function SubjectPage() {
  const { id = '' } = useParams();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const canWrite = useCan('anagrafica.write');
  const canAdmin = useCan('anagrafica.admin');
  const canQuotes = useCan('preventivatore.read');
  const canJobs = useCan('commesse.read');
  const canDocuments = useCan('anagrafica.documents');
  // The account statement (1.47): invoices and balances only with the Tesoreria.
  const canStatement = useCan('tesoreria.read');
  const lookups = useRegistryLookups();
  const subject = useQuery({
    queryKey: registryKeys.subject(id),
    queryFn: () => apiRequest<SubjectResponse>(`/subjects/${id}`),
  });
  const [tab, setTab] = useState<Tab>(
    params.get('scheda') === 'contabile' && canStatement ? 'statement' : 'general',
  );
  const [editing, setEditing] = useState(false);
  const [addingRole, setAddingRole] = useState(params.get('ruolo') === 'nuovo');
  const [confirmActive, setConfirmActive] = useState(false);
  // Documenti (decision of 26/09/2026): today only the quotes, so only for a
  // client and for who sees the quotes.
  // Documenti (ADR-009, ADR-010): the files of every subject for who has the
  // feature «Documenti»; the quotes of a client for who sees the quotes.
  const showQuotes = canQuotes && !!subject.data?.roles.some((role) => role.roleCode === 'C');
  const showFiles = canDocuments && !!subject.data;
  const isClient = !!subject.data?.roles.some((role) => role.roleCode === 'C');
  const showJobs = canJobs && isClient;
  const showDocuments = showQuotes || showFiles || showJobs;
  const subjectQuotes = useSubjectQuotes(id, showQuotes);
  const subjectJobs = useSubjectJobs(id, showJobs);
  const subjectFiles = useSubjectFiles(id, showFiles);
  const documentCount =
    (showQuotes ? (subjectQuotes.data?.length ?? 0) : 0) +
    (showJobs ? (subjectJobs.data?.length ?? 0) : 0) +
    (showFiles ? (subjectFiles.data?.items.length ?? 0) : 0);

  usePageTitle(subject.data?.name ?? null);

  const refresh = async (updated?: SubjectResponse) => {
    if (updated) queryClient.setQueryData(registryKeys.subject(id), updated);
    await queryClient.invalidateQueries({ queryKey: registryKeys.all });
  };
  const toggleActive = useMutation({
    mutationFn: () =>
      apiRequest<SubjectResponse>(`/subjects/${id}/active`, {
        method: 'POST',
        body: { active: !subject.data!.active },
      }),
    onSuccess: async (updated) => {
      setConfirmActive(false);
      await refresh(updated);
    },
  });

  const data = subject.data;
  const commands: (Command | false | undefined)[] = [
    canWrite &&
      data && {
        key: 'edit',
        label: 'Modifica',
        icon: 'edit',
        primary: true,
        onClick: () => {
          setTab('general');
          setEditing(true);
        },
      },
    canWrite &&
      data && {
        key: 'role',
        label: 'Aggiungi ruolo',
        icon: 'plus',
        onClick: () => setAddingRole(true),
      },
    canAdmin &&
      data && {
        key: 'active',
        label: data.active ? 'Disattiva' : 'Riattiva',
        icon: 'close',
        onClick: () => setConfirmActive(true),
      },
  ];
  useCommands(commands.filter((command): command is Command => Boolean(command)));

  if (subject.isPending || lookups.isPending) return <p className="muted">Caricamento…</p>;
  if (subject.isError) {
    return (
      <section className="stack">
        <p className="error">{subject.error.message}</p>
        <button type="button" className="link" onClick={() => navigate('/anagrafica')}>
          Torna all’anagrafica
        </button>
      </section>
    );
  }
  if (!data || !lookups.data) return null;

  const tabs: [Tab, ReactNode][] = [
    ['general', 'Dati generali'],
    ...data.roles.map((role): [Tab, ReactNode] => [
      `role:${role.roleCode}`,
      <>
        {role.roleName} <span className="code muted">{role.code}</span>
      </>,
    ]),
    ['classifications', 'Classificazioni'],
    ['contacts', `Contatti (${data.contacts.length})`],
    ['aliases', 'Alias'],
    ...(showDocuments ? [['documents', `Documenti (${documentCount})`] as [Tab, ReactNode]] : []),
    ...(canStatement ? [['statement', 'Scheda contabile'] as [Tab, ReactNode]] : []),
    ['history', 'Storico'],
  ];

  return (
    <section className="stack">
      <div className="subject-head">
        <div className="subject-mark" aria-hidden="true">
          {initials(data.name)}
        </div>
        <div className="stack tight">
          <h1>{data.name}</h1>
          <div className="flags">
            {data.roles.map((role) => (
              <RoleBadge key={role.code} role={role} withCode />
            ))}
            <StatePill subject={data} />
          </div>
          <div className="muted small subject-meta">
            <span>{lookupLabel(lookups.data.types, data.typeCode)}</span>
            {data.vatNumber && (
              <span>
                P.IVA <span className="code">{data.vatNumber}</span>
              </span>
            )}
            {data.taxCode && data.taxCode !== data.vatNumber && (
              <span>
                C.F. <span className="code">{data.taxCode}</span>
              </span>
            )}
            <span>
              {[data.city, data.province && `(${data.province})`].filter(Boolean).join(' ')} ·{' '}
              {COUNTRIES.find((country) => country.code === data.country)?.name ?? data.country}
            </span>
          </div>
        </div>
      </div>
      {!data.complete && (
        <p className="notice">
          <strong>Soggetto da completare:</strong> manca l’identificativo fiscale. Si consulta, ma
          non si usa per nuovi documenti finché un amministratore non lo completa.
        </p>
      )}
      {data.hasHiddenRoles && (
        <p className="notice info">Il soggetto ha anche ruoli che il tuo profilo non vede.</p>
      )}
      {data.vatGroup.length > 0 && (
        <p className="notice info">
          <strong>Gruppo IVA {data.vatNumber}</strong>: stessa partita IVA, codici fiscali diversi.
          Società collegate ma distinte:{' '}
          {data.vatGroup.map((item, index) => (
            <span key={item.id}>
              {index > 0 && ' · '}
              <Link to={`/anagrafica/${item.id}`}>{item.name}</Link>
              {item.taxCode && <span className="muted"> (CF {item.taxCode})</span>}
            </span>
          ))}
        </p>
      )}

      <nav className="subtabs" aria-label="Sezioni del soggetto">
        {tabs.map(([key, label]) => (
          <button
            key={key}
            type="button"
            className={tab === key ? 'subtab selected' : 'subtab'}
            onClick={() => {
              setTab(key);
              setEditing(false);
            }}
          >
            {label}
          </button>
        ))}
      </nav>

      {tab === 'general' &&
        (editing ? (
          <GeneralForm
            subject={data}
            lookups={lookups.data}
            onDone={async (updated) => {
              setEditing(false);
              if (updated) await refresh(updated);
            }}
          />
        ) : (
          <GeneralView subject={data} lookups={lookups.data} onChanged={refresh} />
        ))}
      {tab.startsWith('role:') && (
        <RoleView
          key={tab}
          subject={data}
          role={data.roles.find((role) => `role:${role.roleCode}` === tab)!}
          lookups={lookups.data}
          onChanged={refresh}
        />
      )}
      {tab === 'classifications' && (
        <ClassificationsView subject={data} lookups={lookups.data} onChanged={refresh} />
      )}
      {tab === 'contacts' && <ContactsView subject={data} onChanged={refresh} />}
      {tab === 'aliases' && <AliasesView subject={data} onChanged={refresh} />}
      {tab === 'documents' && showDocuments && (
        <SubjectDocuments
          subjectId={id}
          showFiles={showFiles}
          showQuotes={showQuotes}
          showJobs={showJobs}
        />
      )}
      {tab === 'statement' && canStatement && <StatementView subjectId={id} />}
      {tab === 'history' && <HistoryView id={id} />}

      {addingRole && (
        <AddRoleDialog
          subject={data}
          lookups={lookups.data}
          onClose={() => {
            setAddingRole(false);
            if (params.has('ruolo')) setParams({}, { replace: true });
          }}
          onAdded={async (updated, roleCode) => {
            setAddingRole(false);
            await refresh(updated);
            setTab(`role:${roleCode}`);
          }}
        />
      )}
      {confirmActive && (
        <ConfirmDialog
          title={data.active ? `Disattivare «${data.name}»?` : `Riattivare «${data.name}»?`}
          confirmLabel={data.active ? 'Disattiva' : 'Riattiva'}
          busy={toggleActive.isPending}
          error={toggleActive.error?.message}
          onConfirm={() => toggleActive.mutate()}
          onCancel={() => setConfirmActive(false)}
        >
          <p>
            {data.active
              ? 'Resta sui documenti dove compare, ma non si propone più per i nuovi. Si può riattivare.'
              : 'Torna disponibile per i nuovi documenti.'}
          </p>
        </ConfirmDialog>
      )}
    </section>
  );
}

function initials(name: string): string {
  return name
    .split(/[\s—-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0])
    .join('')
    .toUpperCase();
}

function Pairs({ rows }: { rows: [string, ReactNode][] }) {
  return (
    <dl className="pairs">
      {rows.map(([label, value]) => (
        <div key={label}>
          <dt>{label}</dt>
          <dd>
            {value === null || value === undefined || value === '' ? (
              <span className="muted">—</span>
            ) : (
              value
            )}
          </dd>
        </div>
      ))}
    </dl>
  );
}

function GeneralView({
  subject,
  lookups,
  onChanged,
}: {
  subject: SubjectResponse;
  lookups: RegistryLookups;
  onChanged: (updated?: SubjectResponse) => Promise<void>;
}) {
  const canAdmin = useCan('anagrafica.admin');
  return (
    <div className="cards">
      <section className="card">
        <h3>Identificazione</h3>
        <Pairs
          rows={[
            ['Tipologia', lookupLabel(lookups.types, subject.typeCode)],
            ['Ragione sociale', subject.name],
            [
              'Partita IVA',
              subject.vatNumber && <span className="code">{subject.vatNumber} 🔒</span>,
            ],
            [
              'Codice fiscale',
              subject.taxCode && <span className="code">{subject.taxCode} 🔒</span>,
            ],
            ['ID Gestify', subject.gestifyId],
          ]}
        />
      </section>
      <section className="card">
        <h3>Sede legale e recapiti</h3>
        <Pairs
          rows={[
            ['Indirizzo', subject.address],
            [
              'CAP e comune',
              [subject.postalCode, subject.city, subject.province && `(${subject.province})`]
                .filter(Boolean)
                .join(' '),
            ],
            ['E-mail', subject.email],
            ['PEC', subject.pec],
            ['Telefono', subject.phone],
            ['Sito', subject.website],
          ]}
        />
      </section>
      <section className="card">
        <h3>Fatturazione elettronica</h3>
        <Pairs
          rows={[
            [
              'Codice destinatario',
              subject.sdiCode && <span className="code">{subject.sdiCode}</span>,
            ],
            ['PEC', subject.pec],
            ['Autofattura', subject.selfInvoicing ? 'Sì (reverse charge)' : 'No'],
            ['1.000 dipendenti o più', subject.largeCompany ? 'Sì' : 'No'],
            ['Società collegata', subject.linkedCompany ? 'Sì' : 'No'],
            [
              'Emette fattura',
              subject.noInvoice
                ? `No${subject.noInvoiceDocument ? ` · manda: ${NO_INVOICE_DOCUMENT_LABELS[subject.noInvoiceDocument].toLowerCase()}` : ''}`
                : 'Sì',
            ],
          ]}
        />
        {subject.notes && (
          <>
            <h3>Note</h3>
            <p className="pre-line">{subject.notes}</p>
          </>
        )}
      </section>
      {canAdmin ? (
        <VisibilityCard subject={subject} onChanged={onChanged} />
      ) : (
        subject.owner && (
          <section className="card">
            <h3>In carico a</h3>
            <p>{subject.owner.name}</p>
          </section>
        )
      )}
    </div>
  );
}

function GeneralForm({
  subject,
  lookups,
  onDone,
}: {
  subject: SubjectResponse;
  lookups: RegistryLookups;
  onDone: (updated?: SubjectResponse) => Promise<void>;
}) {
  const canAdmin = useCan('anagrafica.admin');
  const [form, setForm] = useState({
    typeCode: subject.typeCode,
    name: subject.name,
    country: subject.country,
    vatNumber: subject.vatNumber ?? '',
    taxCode: subject.taxCode ?? '',
    address: subject.address ?? '',
    postalCode: subject.postalCode ?? '',
    city: subject.city ?? '',
    province: subject.province ?? '',
    email: subject.email ?? '',
    pec: subject.pec ?? '',
    phone: subject.phone ?? '',
    website: subject.website ?? '',
    sdiCode: subject.sdiCode ?? '',
    notes: subject.notes ?? '',
  });
  const [selfInvoicing, setSelfInvoicing] = useState(subject.selfInvoicing);
  const [largeCompany, setLargeCompany] = useState(subject.largeCompany);
  const [linkedCompany, setLinkedCompany] = useState(subject.linkedCompany);
  const [noInvoice, setNoInvoice] = useState(subject.noInvoice);
  const [noInvoiceDocument, setNoInvoiceDocument] = useState<NoInvoiceDocument | ''>(
    subject.noInvoiceDocument ?? '',
  );
  const [errors, setErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: (body: unknown) =>
      apiRequest<SubjectResponse>(`/subjects/${subject.id}`, { method: 'PATCH', body }),
    onSuccess: (updated) => onDone(updated),
    onError: (error) => showServerError(error, setErrors, setFormError),
  });
  const submit = (event: FormEvent) => {
    event.preventDefault();
    setFormError(null);
    // Without the Amministrazione level the tax identifiers are not sent at all.
    const body = {
      ...form,
      selfInvoicing,
      largeCompany,
      linkedCompany,
      noInvoice,
      noInvoiceDocument: noInvoice ? noInvoiceDocument || null : null,
    };
    save.mutate(canAdmin ? body : { ...body, vatNumber: undefined, taxCode: undefined });
  };
  const text = (key: keyof typeof form, label: string, disabled = false) => (
    <Field label={label} error={errors[key]}>
      <input
        value={form[key]}
        disabled={disabled}
        onChange={(e) => setForm({ ...form, [key]: e.target.value })}
      />
    </Field>
  );

  return (
    <form className="card stack" onSubmit={submit} noValidate>
      <div className="form-grid">
        {text('name', 'Ragione sociale / Cognome e nome')}
        <Field label="Tipologia">
          <select
            value={form.typeCode}
            onChange={(e) => setForm({ ...form, typeCode: e.target.value })}
          >
            {lookups.types.map((type) => (
              <option key={type.code} value={type.code}>
                {type.description}
              </option>
            ))}
          </select>
        </Field>
        {text('vatNumber', 'Partita IVA', !canAdmin)}
        {text('taxCode', 'Codice fiscale', !canAdmin)}
        {text('address', 'Indirizzo')}
        {text('postalCode', 'CAP')}
        {text('city', 'Comune')}
        {text('province', 'Provincia')}
        {text('email', 'E-mail')}
        {text('pec', 'PEC')}
        {text('phone', 'Telefono')}
        {text('website', 'Sito')}
        {text('sdiCode', 'Codice destinatario SDI')}
      </div>
      {!canAdmin && (
        <p className="muted small">
          L’identificativo fiscale si cambia solo con il livello Amministrazione.
        </p>
      )}
      <Field label="Note">
        <textarea
          rows={3}
          value={form.notes}
          onChange={(e) => setForm({ ...form, notes: e.target.value })}
        />
      </Field>
      <label className="checkbox">
        <input
          type="checkbox"
          checked={selfInvoicing}
          onChange={(e) => setSelfInvoicing(e.target.checked)}
        />
        Autofattura (reverse charge): vale per tutti i ruoli del soggetto
      </label>
      <label className="checkbox">
        <input
          type="checkbox"
          checked={largeCompany}
          onChange={(e) => setLargeCompany(e.target.checked)}
        />
        1.000 dipendenti o più: il primo contratto con questo cliente è complesso
      </label>
      <label className="checkbox">
        <input
          type="checkbox"
          checked={linkedCompany}
          onChange={(e) => setLinkedCompany(e.target.checked)}
        />
        Società collegata (socio o società legata a un socio): le sue fatture possono essere
        intergruppo
      </label>
      <label className="checkbox">
        <input
          type="checkbox"
          checked={noInvoice}
          onChange={(e) => setNoInvoice(e.target.checked)}
        />
        Non emette fattura (assicurazioni, consorzi di garanzia, Comuni per le multe): un pagamento
        è il costo, con la voce del ruolo Fornitore
      </label>
      {noInvoice && (
        <Field label="Documento che manda">
          <select
            value={noInvoiceDocument}
            onChange={(e) => setNoInvoiceDocument(e.target.value as NoInvoiceDocument | '')}
          >
            <option value="">—</option>
            {NO_INVOICE_DOCUMENTS.map((item) => (
              <option key={item} value={item}>
                {NO_INVOICE_DOCUMENT_LABELS[item]}
              </option>
            ))}
          </select>
        </Field>
      )}
      {formError && <p className="error">{formError}</p>}
      <div className="actions">
        <button type="button" onClick={() => void onDone()}>
          Annulla
        </button>
        <button type="submit" className="primary" disabled={save.isPending}>
          Salva
        </button>
      </div>
    </form>
  );
}

function VisibilityCard({
  subject,
  onChanged,
}: {
  subject: SubjectResponse;
  onChanged: (updated?: SubjectResponse) => Promise<void>;
}) {
  const users = useQuery({
    queryKey: ['subjects', 'assignable-users'],
    queryFn: () => apiRequest<{ id: string; name: string }[]>('/subjects/assignable-users'),
  });
  const [share, setShare] = useState('');
  const run = useMutation({
    mutationFn: (request: { path: string; method: 'POST' | 'PATCH' | 'DELETE'; body?: unknown }) =>
      apiRequest<SubjectResponse>(`/subjects/${subject.id}${request.path}`, {
        method: request.method,
        body: request.body,
      }),
    onSuccess: (updated) => onChanged(updated),
  });
  const candidates = (users.data ?? []).filter(
    (user) =>
      user.id !== subject.owner?.id && !subject.sharedWith.some((item) => item.id === user.id),
  );
  return (
    <section className="card stack tight">
      <h3>Visibilità</h3>
      <Field label="In carico a" hint="Chi l’ha inserita, o a chi è stata assegnata.">
        <select
          value={subject.owner?.id ?? ''}
          onChange={(e) =>
            run.mutate({ path: '/owner', method: 'PATCH', body: { ownerId: e.target.value } })
          }
        >
          {!subject.owner && <option value="">—</option>}
          {users.data?.map((user) => (
            <option key={user.id} value={user.id}>
              {user.name}
            </option>
          ))}
        </select>
      </Field>
      <div>
        <span className="muted small block">Sbloccata per</span>
        <div className="flags">
          {subject.sharedWith.length === 0 && <span className="muted">nessuno</span>}
          {subject.sharedWith.map((user) => (
            <span key={user.id} className="pill muted">
              {user.name}{' '}
              <button
                type="button"
                className="link"
                aria-label={`Revoca a ${user.name}`}
                onClick={() => run.mutate({ path: `/shares/${user.id}`, method: 'DELETE' })}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      </div>
      <div className="row">
        <select value={share} onChange={(e) => setShare(e.target.value)} aria-label="Sblocca per">
          <option value="">Sblocca per…</option>
          {candidates.map((user) => (
            <option key={user.id} value={user.id}>
              {user.name}
            </option>
          ))}
        </select>
        <button
          type="button"
          className="small"
          disabled={!share}
          onClick={() => {
            run.mutate({ path: '/shares', method: 'POST', body: { userId: share } });
            setShare('');
          }}
        >
          Sblocca
        </button>
      </div>
      {run.error && <p className="error">{run.error.message}</p>}
      <p className="muted small">
        Lo vedono gli amministratori, chi vede tutte le anagrafiche, chi l’ha in carico e chi è
        stato sbloccato.
      </p>
    </section>
  );
}

function RoleView({
  subject,
  role,
  lookups,
  onChanged,
}: {
  subject: SubjectResponse;
  role: SubjectRoleResponse;
  lookups: RegistryLookups;
  onChanged: (updated?: SubjectResponse) => Promise<void>;
}) {
  const canWrite = useCan('anagrafica.write');
  const definition = lookups.roles.find((item) => item.code === role.roleCode);
  const fields = definition?.requiredData ?? [];
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState<Partial<Record<RoleDataField, string | null>>>(role);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: (body: unknown) =>
      apiRequest<SubjectResponse>(`/subjects/${subject.id}/roles/${role.roleCode}`, {
        method: 'PATCH',
        body,
      }),
    onSuccess: async (updated) => {
      setEditing(false);
      await onChanged(updated);
    },
    onError: (error) => showServerError(error, setErrors, setFormError),
  });

  // A new «conto riclassificato» with invoices already registered: asked
  // whether they take it too (1.44).
  const [pastQuestion, setPastQuestion] = useState<{
    body: Record<string, unknown>;
    invoices: number;
  } | null>(null);
  const submit = async (body: Record<string, unknown>) => {
    const changedReclass =
      'reclassAccount' in body && (body.reclassAccount ?? null) !== (role.reclassAccount ?? null);
    if (changedReclass && (role.roleCode === 'C' || role.roleCode === 'F')) {
      try {
        const { invoices } = await apiRequest<{ invoices: number }>(
          `/subjects/${subject.id}/roles/${role.roleCode}/reclass-invoices`,
        );
        if (invoices > 0) {
          setPastQuestion({ body, invoices });
          return;
        }
      } catch {
        // Without the count the change goes on for the next invoices only.
      }
    }
    save.mutate(body);
  };

  const display = (field: RoleDataField): ReactNode => {
    if (!(field in role)) return <span className="muted">Non visibile con il tuo profilo</span>;
    const raw = role[field];
    if (field === 'termsCode') return lookupLabel(lookups.terms, raw);
    if (field === 'methodCode') return lookupLabel(lookups.methods, raw);
    if (field === 'priorityCode')
      return raw ? `${raw} · ${lookupLabel(lookups.priorities, raw)}` : '';
    if (field === 'iban') return raw && <span className="code">{raw}</span>;
    const list = {
      reclassAccount: lookups.reclassAccounts,
      costCenter: lookups.costCenters,
      preferredAccount: lookups.accounts,
    }[field];
    if (!raw || !list) return raw;
    const found = list.find((entry) => entry.code === raw);
    if (!found) return `${raw} (non in tabella)`;
    return field === 'reclassAccount' ? `${raw} · ${found.description}` : found.description;
  };
  const [legacyCode, setLegacyCode] = useState(role.legacyCode ?? '');

  return (
    <div className="cards">
      <section className="card stack">
        <h3>{role.roleName}</h3>
        {editing ? (
          <>
            <RoleFields
              fields={fields}
              lookups={lookups}
              value={value}
              errors={errors}
              onChange={setValue}
            />
            <div className="form-grid">
              <Field
                label="Codice storico"
                error={errors.legacyCode}
                hint="Il codice dell’Excel della Tesoreria (C0001, F0001), se c’era."
              >
                <input
                  className="mono-input"
                  value={legacyCode}
                  onChange={(e) => setLegacyCode(e.target.value.toUpperCase())}
                />
              </Field>
            </div>
            {formError && <p className="error">{formError}</p>}
            {pastQuestion && (
              <PastQuestion
                invoices={pastQuestion.invoices}
                direction={role.roleCode === 'C' ? 'emesse a' : 'ricevute da'}
                busy={save.isPending}
                onAnswer={(toPast) => {
                  save.mutate({ ...pastQuestion.body, reclassToPast: toPast });
                  setPastQuestion(null);
                }}
                onCancel={() => setPastQuestion(null)}
              />
            )}
            <div className="actions">
              <button type="button" onClick={() => setEditing(false)}>
                Annulla
              </button>
              <button
                type="button"
                className="primary"
                disabled={save.isPending}
                onClick={() =>
                  void submit({
                    ...Object.fromEntries(
                      fields
                        .filter((field) => field in value)
                        .map((field) => [field, value[field] ?? null]),
                    ),
                    legacyCode: legacyCode.trim() || null,
                  })
                }
              >
                Salva
              </button>
            </div>
          </>
        ) : (
          <>
            <Pairs
              rows={[
                ['Codice', <span className="code">{role.code} 🔒</span>],
                ...(role.legacyCode
                  ? [
                      ['Codice storico', <span className="code">{role.legacyCode}</span>] as [
                        string,
                        ReactNode,
                      ],
                    ]
                  : []),
                [
                  'Stato',
                  role.active ? (
                    <span className="pill ok">Attivo</span>
                  ) : (
                    <span className="pill muted">Disattivato</span>
                  ),
                ],
                ...fields.map((field): [string, ReactNode] => [
                  ROLE_FIELD_LABELS[field],
                  display(field),
                ]),
              ]}
            />
            {canWrite && (
              <div className="row">
                {fields.length > 0 && (
                  <button type="button" className="small" onClick={() => setEditing(true)}>
                    Modifica i dati del ruolo
                  </button>
                )}
                <button
                  type="button"
                  className="small"
                  onClick={() => save.mutate({ active: !role.active })}
                >
                  {role.active ? 'Disattiva il ruolo' : 'Riattiva il ruolo'}
                </button>
              </div>
            )}
          </>
        )}
      </section>
      {role.roleCode === 'C' && (
        <ClientPriceLists subject={subject} role={role} lookups={lookups} onChanged={onChanged} />
      )}
      <section className="card">
        <h3>Da dove vengono questi campi</h3>
        <p className="muted">
          I dati della scheda li decide il ruolo «{role.roleName}» nella tabella Ruoli anagrafici.
          {definition?.reserved &&
            ' È un ruolo riservato: lo vede solo chi ha la funzione concessa.'}{' '}
          Il codice {role.code} resta di questo soggetto anche se il ruolo si disattiva.
        </p>
      </section>
    </div>
  );
}

function ClassificationsView({
  subject,
  lookups,
  onChanged,
}: {
  subject: SubjectResponse;
  lookups: RegistryLookups;
  onChanged: (updated?: SubjectResponse) => Promise<void>;
}) {
  const canWrite = useCan('anagrafica.write');
  const save = useMutation({
    mutationFn: (body: Record<string, string>) =>
      apiRequest<SubjectResponse>(`/subjects/${subject.id}/classifications`, {
        method: 'PUT',
        body,
      }),
    onSuccess: (updated) => onChanged(updated),
  });
  if (lookups.classifications.length === 0) {
    return (
      <p className="card muted">
        Nessuna classificazione: si creano in Tabelle come tabelle libere segnate «classificazione
        dei soggetti».
      </p>
    );
  }
  return (
    <section className="card stack">
      <div className="form-grid">
        {lookups.classifications.map((table) => (
          <Field key={table.key} label={table.name}>
            <select
              value={subject.classifications[table.key] ?? ''}
              disabled={!canWrite || save.isPending}
              onChange={(e) => {
                const next = { ...subject.classifications };
                if (e.target.value) next[table.key] = e.target.value;
                else delete next[table.key];
                save.mutate(next);
              }}
            >
              <option value="">—</option>
              {table.entries.map((entry) => (
                <option key={entry.code} value={entry.code}>
                  {entry.description}
                </option>
              ))}
            </select>
          </Field>
        ))}
      </div>
      {save.error && <p className="error">{save.error.message}</p>}
    </section>
  );
}

function ContactsView({
  subject,
  onChanged,
}: {
  subject: SubjectResponse;
  onChanged: (updated?: SubjectResponse) => Promise<void>;
}) {
  const canWrite = useCan('anagrafica.write');
  const [editing, setEditing] = useState<ContactResponse | 'new' | null>(null);
  const remove = useMutation({
    mutationFn: (contactId: string) =>
      apiRequest<SubjectResponse>(`/subjects/${subject.id}/contacts/${contactId}`, {
        method: 'DELETE',
      }),
    onSuccess: (updated) => onChanged(updated),
  });
  return (
    <section className="stack">
      {canWrite && (
        <div>
          <button type="button" className="small" onClick={() => setEditing('new')}>
            + Nuovo contatto
          </button>
        </div>
      )}
      <div className="table-wrap card flush">
        <table className="data-table">
          <thead>
            <tr>
              <th>Nome</th>
              <th>Ruolo</th>
              <th>E-mail</th>
              <th>Telefono</th>
              <th aria-label="Azioni" />
            </tr>
          </thead>
          <tbody>
            {subject.contacts.map((contact) => (
              <tr key={contact.id}>
                <td>
                  <strong>{contact.name}</strong>
                  {contact.isPrimary && <span className="tag">principale</span>}
                </td>
                <td>{contact.title}</td>
                <td>{contact.email}</td>
                <td>{contact.phone}</td>
                <td className="grid-actions">
                  {canWrite && (
                    <>
                      <button type="button" className="link" onClick={() => setEditing(contact)}>
                        Modifica
                      </button>
                      <button
                        type="button"
                        className="link"
                        onClick={() => remove.mutate(contact.id)}
                      >
                        Elimina
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}
            {subject.contacts.length === 0 && (
              <tr>
                <td colSpan={5} className="grid-empty">
                  Nessun contatto.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {editing && (
        <ContactDialog
          subjectId={subject.id}
          contact={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={async (updated) => {
            setEditing(null);
            await onChanged(updated);
          }}
        />
      )}
    </section>
  );
}

function ContactDialog({
  subjectId,
  contact,
  onClose,
  onSaved,
}: {
  subjectId: string;
  contact: ContactResponse | null;
  onClose: () => void;
  onSaved: (updated: SubjectResponse) => Promise<void>;
}) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [form, setForm] = useState({
    name: contact?.name ?? '',
    title: contact?.title ?? '',
    email: contact?.email ?? '',
    phone: contact?.phone ?? '',
    isPrimary: contact?.isPrimary ?? false,
  });
  const [errors, setErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  useEffect(() => {
    dialogRef.current?.showModal();
  }, []);
  const save = useMutation({
    mutationFn: () =>
      apiRequest<SubjectResponse>(
        contact
          ? `/subjects/${subjectId}/contacts/${contact.id}`
          : `/subjects/${subjectId}/contacts`,
        { method: contact ? 'PATCH' : 'POST', body: form },
      ),
    onSuccess: (updated) => onSaved(updated),
    onError: (error) => showServerError(error, setErrors, setFormError),
  });
  return (
    <dialog ref={dialogRef} className="dialog narrow" onClose={onClose}>
      <form
        className="stack"
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        <h2>{contact ? 'Modifica contatto' : 'Nuovo contatto'}</h2>
        <Field label="Nome" error={errors.name}>
          <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </Field>
        <Field label="Ruolo presso il soggetto" error={errors.title}>
          <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
        </Field>
        <Field label="E-mail" error={errors.email}>
          <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        </Field>
        <Field label="Telefono" error={errors.phone}>
          <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
        </Field>
        <label className="checkbox">
          <input
            type="checkbox"
            checked={form.isPrimary}
            onChange={(e) => setForm({ ...form, isPrimary: e.target.checked })}
          />
          Contatto principale
        </label>
        {formError && <p className="error">{formError}</p>}
        <div className="actions">
          <button type="button" onClick={onClose}>
            Annulla
          </button>
          <button type="submit" className="primary" disabled={save.isPending}>
            Salva
          </button>
        </div>
      </form>
    </dialog>
  );
}

function AliasesView({
  subject,
  onChanged,
}: {
  subject: SubjectResponse;
  onChanged: (updated?: SubjectResponse) => Promise<void>;
}) {
  const canWrite = useCan('anagrafica.write');
  const [text, setText] = useState('');
  const save = useMutation({
    mutationFn: (aliases: string[]) =>
      apiRequest<SubjectResponse>(`/subjects/${subject.id}/aliases`, {
        method: 'PUT',
        body: { aliases },
      }),
    onSuccess: (updated) => onChanged(updated),
  });
  return (
    <section className="card stack">
      <p className="muted">
        Nomi con cui il soggetto compare nelle fatture o in altri gestionali: la ricerca li trova,
        anche in Tesoreria, e la riconciliazione con la banca e le carte li riconosce. Quelli
        confermati da «Anagrafica dalle fatture» dicono da quale fattura vengono.
      </p>
      <div className="flags">
        {subject.aliases.length === 0 && <span className="muted">Nessun alias.</span>}
        {subject.aliases.map((alias) => (
          <span
            key={alias}
            className="pill muted"
            title={subject.aliasSources[alias] ?? 'scritto a mano'}
          >
            {alias}
            {subject.aliasSources[alias] && (
              <small className="muted"> · {subject.aliasSources[alias]}</small>
            )}{' '}
            {canWrite && (
              <button
                type="button"
                className="link"
                aria-label={`Togli ${alias}`}
                onClick={() => save.mutate(subject.aliases.filter((item) => item !== alias))}
              >
                ×
              </button>
            )}
          </span>
        ))}
      </div>
      {canWrite && (
        <form
          className="row"
          onSubmit={(e) => {
            e.preventDefault();
            if (text.trim()) save.mutate([...subject.aliases, text.trim()]);
            setText('');
          }}
        >
          <input
            value={text}
            placeholder="Nuovo alias"
            aria-label="Nuovo alias"
            onChange={(e) => setText(e.target.value)}
          />
          <button type="submit" className="small">
            Aggiungi
          </button>
        </form>
      )}
      {save.error && <p className="error">{save.error.message}</p>}
    </section>
  );
}

function HistoryView({ id }: { id: string }) {
  const history = useQuery({
    queryKey: registryKeys.history(id),
    queryFn: () => apiRequest<AuditEventResponse[]>(`/subjects/${id}/history`),
  });
  return (
    <section className="card">
      {history.isPending && <p className="muted">Caricamento…</p>}
      <ul className="timeline">
        {history.data?.map((event) => (
          <li key={event.id}>
            <span className="muted small">
              {formatTimestamp(event.occurredAt)}
              <br />
              {event.actorEmail ?? '—'}
            </span>
            <span>
              {event.summary}
              {describeAuditDetails(event).map((line) => (
                <small key={line} className="muted block">
                  {line}
                </small>
              ))}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

function AddRoleDialog({
  subject,
  lookups,
  onClose,
  onAdded,
}: {
  subject: SubjectResponse;
  lookups: RegistryLookups;
  onClose: () => void;
  onAdded: (updated: SubjectResponse, roleCode: string) => Promise<void>;
}) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const canSuppliers = useCan('anagrafica.suppliers');
  const canReserved = useCan('anagrafica.reserved-roles');
  const available = lookups.roles.filter(
    (role) =>
      !subject.roles.some((item) => item.roleCode === role.code) &&
      (!role.reserved || canReserved) &&
      (role.code !== 'F' || canSuppliers),
  );
  const [roleCode, setRoleCode] = useState(available[0]?.code ?? '');
  const [value, setValue] = useState<Partial<Record<RoleDataField, string>>>({});
  const [errors, setErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  useEffect(() => {
    dialogRef.current?.showModal();
  }, []);
  const add = useMutation({
    mutationFn: () =>
      apiRequest<SubjectResponse>(`/subjects/${subject.id}/roles`, {
        method: 'POST',
        body: { roleCode, ...value },
      }),
    onSuccess: (updated) => onAdded(updated, roleCode),
    onError: (error) => showServerError(error, setErrors, setFormError),
  });
  const role = available.find((item) => item.code === roleCode);

  return (
    <dialog ref={dialogRef} className="dialog" onClose={onClose}>
      <form
        className="stack"
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          add.mutate();
        }}
      >
        <h2>Aggiungi ruolo</h2>
        <p className="muted">
          Il ruolo si aggiunge a «{subject.name}»: stessa P.IVA, stessa sede, nessun doppione.
        </p>
        {available.length === 0 ? (
          <p className="muted">
            Il soggetto ha già tutti i ruoli che il tuo profilo può assegnare.
          </p>
        ) : (
          <>
            <Field label="Ruolo">
              <select
                value={roleCode}
                onChange={(e) => {
                  setRoleCode(e.target.value);
                  setValue({});
                }}
              >
                {available.map((item) => (
                  <option key={item.code} value={item.code}>
                    {item.description} (codice {item.prefix}…)
                  </option>
                ))}
              </select>
            </Field>
            {role && (
              <RoleFields
                fields={role.requiredData}
                lookups={lookups}
                value={value}
                errors={errors}
                onChange={setValue}
              />
            )}
          </>
        )}
        {formError && <p className="error">{formError}</p>}
        <div className="actions">
          <button type="button" onClick={onClose}>
            Annulla
          </button>
          {available.length > 0 && (
            <button type="submit" className="primary" disabled={add.isPending}>
              Aggiungi ruolo
            </button>
          )}
        </div>
      </form>
    </dialog>
  );
}

/** «Applicare la nuova voce anche al pregresso?» (1.44). */
function PastQuestion({
  invoices,
  direction,
  busy,
  onAnswer,
  onCancel,
}: {
  invoices: number;
  direction: string;
  busy: boolean;
  onAnswer: (toPast: boolean) => void;
  onCancel: () => void;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    ref.current?.showModal();
  }, []);
  return (
    <dialog ref={ref} className="dialog narrow" onClose={onCancel}>
      <div className="stack">
        <h2>Cambi il conto riclassificato</h2>
        <p>
          Applicare la nuova voce anche al <strong>pregresso</strong>?{' '}
          {invoices === 1
            ? `C’è 1 fattura ${direction === 'emesse a' ? 'emessa a' : 'ricevuta da'} questo soggetto che oggi prende la voce dall’anagrafica.`
            : `Ci sono ${invoices} fatture ${direction} questo soggetto che oggi prendono la voce dall’anagrafica.`}{' '}
          Quelle con una voce scelta a mano non cambiano in nessun caso.
        </p>
        <div className="actions">
          <button type="button" onClick={onCancel} disabled={busy}>
            Annulla
          </button>
          <button type="button" onClick={() => onAnswer(false)} disabled={busy}>
            Solo dalle prossime
          </button>
          <button type="button" className="primary" onClick={() => onAnswer(true)} disabled={busy}>
            Sì, anche il pregresso
          </button>
        </div>
      </div>
    </dialog>
  );
}
